/**
 * Profile Section Component
 * Displays user profile information and linked accounts
 */

import React from 'react';
import styled from 'styled-components';

const Section = styled.div`
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: var(--accent-primary);
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.span`
  font-size: 1.1rem;
  color: var(--text-primary);
  padding: 0.75rem;
  background: var(--bg-secondary);
  border-radius: 6px;
  border: 1px solid var(--border-color);
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    switch(props.type) {
      case 'admin': return '#4CAF50';
      case 'student': return '#2196F3';
      case 'approved': return '#4CAF50';
      case 'pending': return '#FF9800';
      default: return '#9E9E9E';
    }
  }};
  color: white;
  margin-left: 0.5rem;
`;

const LinkedAccounts = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
`;

const AccountCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  margin-bottom: 1rem;
`;

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AccountIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const AccountIconDisabled = styled(AccountIcon)`
  background: #e0e0e0;
`;

const AccountIconPassword = styled(AccountIcon)`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
`;

const DisabledText = styled.span`
  opacity: 0.5;
`;

const AccountDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const AccountName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const AccountStatus = styled.span`
  font-size: 0.85rem;
  color: var(--text-primary);
  opacity: 0.6;
`;

function ProfileSection({ user }) {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Section>
        <SectionTitle>
          <span>👤</span>
          Profile Information
        </SectionTitle>

        <InfoGrid>
          <InfoItem>
            <Label>Email Address</Label>
            <Value>{user.email}</Value>
          </InfoItem>

          <InfoItem>
            <Label>Account Type</Label>
            <Value>
              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              <Badge type={user.role}>{user.role}</Badge>
            </Value>
          </InfoItem>

          <InfoItem>
            <Label>Account Status</Label>
            <Value>
              {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
              <Badge type={user.status}>{user.status}</Badge>
            </Value>
          </InfoItem>

          <InfoItem>
            <Label>Member Since</Label>
            <Value>{formatDate(user.createdAt)}</Value>
          </InfoItem>

          {user.courseAccess && user.courseAccess.length > 0 && (
            <InfoItem>
              <Label>Course Access</Label>
              <Value>
                {user.courseAccess.map(course => course.toUpperCase()).join(', ')}
              </Value>
            </InfoItem>
          )}
        </InfoGrid>

        <LinkedAccounts>
          <SectionTitle>
            <span>🔗</span>
            Linked Accounts
          </SectionTitle>

          {user.googleId && (
            <AccountCard>
              <AccountInfo>
                <AccountIcon>
                  <span>G</span>
                </AccountIcon>
                <AccountDetails>
                  <AccountName>Google Account</AccountName>
                  <AccountStatus>Connected • OAuth Login Enabled</AccountStatus>
                </AccountDetails>
              </AccountInfo>
              <Badge type="approved">Connected</Badge>
            </AccountCard>
          )}

          {!user.googleId && (
            <AccountCard>
              <AccountInfo>
                <AccountIconDisabled>
                  <DisabledText>G</DisabledText>
                </AccountIconDisabled>
                <AccountDetails>
                  <AccountName>Google Account</AccountName>
                  <AccountStatus>Not linked • Link for faster login</AccountStatus>
                </AccountDetails>
              </AccountInfo>
              <Badge type="pending">Not Connected</Badge>
            </AccountCard>
          )}

          {user.hasPassword && (
            <AccountCard>
              <AccountInfo>
                <AccountIconPassword>
                  <span>🔑</span>
                </AccountIconPassword>
                <AccountDetails>
                  <AccountName>Password Login</AccountName>
                  <AccountStatus>Enabled • Change in Password tab</AccountStatus>
                </AccountDetails>
              </AccountInfo>
              <Badge type="approved">Active</Badge>
            </AccountCard>
          )}

          {!user.hasPassword && (
            <AccountCard>
              <AccountInfo>
                <AccountIconDisabled>
                  <DisabledText>🔑</DisabledText>
                </AccountIconDisabled>
                <AccountDetails>
                  <AccountName>Password Login</AccountName>
                  <AccountStatus>Not set • OAuth login only</AccountStatus>
                </AccountDetails>
              </AccountInfo>
              <Badge type="pending">Not Set</Badge>
            </AccountCard>
          )}
        </LinkedAccounts>
      </Section>
    </>
  );
}

export default ProfileSection;
